import { createSlice } from "@reduxjs/toolkit";
import { logout } from "./authSlice";

const STORAGE_KEY = "ordersCache";

const emptyCache = {
  outletId: null,
  day: null,
  orders: [],
  lastRetrievedTime: null,
  lastOnlineRetrievedTime: null,
};

const loadCache = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...emptyCache, ...JSON.parse(stored) } : emptyCache;
  } catch (e) {
    return emptyCache;
  }
};

const saveCache = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error("Unable to persist orders cache:", e.message);
  }
};

// The api picks the outlet from the server-side selection, which switches
// before redux does (and can be switched from another tab), so a response
// may carry another outlet's orders. Orders that carry no outletId cannot
// be checked and are kept
export const belongsToOutlet = (order, outletId) => {
  const orderOutletId = order?.outletId?._id ?? order?.outletId;
  return (
    orderOutletId === undefined ||
    orderOutletId === null ||
    String(orderOutletId) === String(outletId)
  );
};

const latestUpdatedAt = (orders, fallback) =>
  orders.reduce(
    (latest, order) =>
      order?.updatedAt && (!latest || order.updatedAt > latest)
        ? order.updatedAt
        : latest,
    fallback
  );

// Today's orders of the selected outlet, cached so the home page only asks
// the api for orders updated since the last retrieval.
// The timestamps are the newest updatedAt seen from the server (not the
// browser clock), so a wrong device clock can never skip an order.
const ordersCacheSlice = createSlice({
  name: "ordersCache",
  initialState: loadCache(),
  reducers: {
    mergeOrders: (state, action) => {
      const { outletId, day, reset, onlineOnly, dayStart } = action.payload;
      const orders = action.payload.orders.filter((order) =>
        belongsToOutlet(order, outletId)
      );

      if (reset) {
        Object.assign(state, emptyCache, { outletId, day });
      } else if (state.outletId !== outletId || state.day !== day) {
        return;
      }

      const byId = new Map(state.orders.map((order) => [order._id, order]));
      orders.forEach((order) => byId.set(order._id, order));
      state.orders = Array.from(byId.values()).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      if (onlineOnly) {
        state.lastOnlineRetrievedTime = latestUpdatedAt(
          orders,
          state.lastOnlineRetrievedTime || dayStart
        );
      } else {
        state.lastRetrievedTime = latestUpdatedAt(
          orders,
          state.lastRetrievedTime || dayStart
        );
        // A full fetch covers online orders too, so the online cursor can
        // never lag behind the full one
        if (
          !state.lastOnlineRetrievedTime ||
          state.lastOnlineRetrievedTime < state.lastRetrievedTime
        ) {
          state.lastOnlineRetrievedTime = state.lastRetrievedTime;
        }
      }

      saveCache(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.error("Unable to clear orders cache:", e.message);
      }
      return emptyCache;
    });
  },
});

export const { mergeOrders } = ordersCacheSlice.actions;

export default ordersCacheSlice.reducer;
